import { List, Map } from "immutable";
import { createSelector } from "reselect";

import { SemanticCategory } from "@seneca/tags-service-consts";

import { composeSelector } from "seneca-common/utils/selectors/compose-selectors";

import { PATH_TAGS_FEATURE } from "../consts";
import Category from "../models/Category";
import Tag from "../models/Tag";
import TagsFeature from "../models/TagsFeature";
import * as categories from "./categories";
import * as tags from "./tags";

type State = any;
type CategoryId = string;
type TagId = string;
type Categories = Map<CategoryId, Category>;
export type Tags = Map<TagId, Tag>;
type Name = string;

export function getTagsFeatureSlice(state: any): TagsFeature {
  return state.get(PATH_TAGS_FEATURE);
}

export function makeSelectors(getStateSlice: (arg0: any) => TagsFeature) {
  const getCategoriesSelector = (state: TagsFeature) => {
    return state.categories;
  };

  const getTagsSelector = (state: TagsFeature) => {
    return state.tags;
  };

  const getStateTags: (arg0: State) => Tags = composeSelector(
    getStateSlice,
    getTagsSelector
  );

  const getStateCategories: (arg0: State) => Categories = composeSelector(
    getStateSlice,
    getCategoriesSelector
  );

  const getCategoryIds: (arg0: State) => CategoryId[] = composeSelector(
    // @ts-ignore
    getStateCategories,
    categories.getCategoryIds
  );

  // @ts-ignore
  const getRootTag: (arg0: State) => Tag = createSelector(
    getStateTags,
    tags.getRootTag
  );

  const hasRootTag: (arg0: State) => boolean = createSelector(
    getRootTag,
    root => !!root
  );

  // @ts-ignore
  const getRootCategoryIds: (arg0: State) => CategoryId[] = createSelector(
    getStateTags,
    tags.getRootCategoryIds
  );

  const getCategory: (arg0: State, arg1: CategoryId) => Category | undefined =
    // @ts-ignore
    composeSelector(getStateCategories, categories.getCategory);

  const getCategoryName: (arg0: State, arg1: CategoryId) => string =
    // @ts-ignore
    composeSelector(getStateCategories, categories.getCategoryName);

  const getTagName: (arg0: State, arg1: TagId) => string = composeSelector(
    getStateTags,
    tags.getTagName
  );

  // @ts-ignore
  const getTagDescription: (arg0: State, arg1: TagId) => string =
    composeSelector(getStateTags, tags.getTagDescription);

  // @ts-ignore
  const getTagSubjectGroup: (arg0: State, arg1: TagId) => string =
    composeSelector(getStateTags, tags.getTagSubjectGroup);

  // @ts-ignore
  const getTagCoursesFilterOnly: (arg0: State, arg1: TagId) => boolean =
    composeSelector(getStateTags, tags.getTagCoursesFilterOnly);

  // @ts-ignore
  const getTagCategoryIds: (arg0: State, arg1: TagId) => CategoryId[] =
    composeSelector(getStateTags, tags.getCategoryIds);

  // @ts-ignore
  const getVisibleTagCategoryIds: (arg0: State, arg1: TagId) => CategoryId[] =
    createSelector(
      // @ts-ignore
      getStateCategories,
      getTagCategoryIds,
      categories.getVisibleCategoryIds
    );

  // @ts-ignore
  const getVisibleRootCategoryIds: (arg0: State) => CategoryId[] =
    createSelector(
      // @ts-ignore
      getStateCategories,
      getRootCategoryIds,
      categories.getVisibleCategoryIds
    );

  // @ts-ignore
  const getVisibleRootCategoryIdsWithoutPrice: (arg0: State) => CategoryId[] =
    createSelector(
      // @ts-ignore
      getStateCategories,
      getRootCategoryIds,
      categories.getVisibleCategoryIdsWithoutPrice
    );

  const getFirstRootCategoryId: (arg0: State) => CategoryId = composeSelector(
    getVisibleRootCategoryIds,
    // @ts-ignore
    categoryIds => categoryIds.get(0)
  );

  const getTagDetails: (arg0: State, arg1: TagId) => object = composeSelector(
    getStateTags,
    tags.getTagDetails
  );

  // @ts-ignore
  const getCategoryDetails: (arg0: State, arg1: CategoryId) => object =
    // @ts-ignore
    composeSelector(getStateCategories, categories.getCategoryDetails);

  // @ts-ignore
  const applyPremiumStyleToFilter: (arg0: State, arg1: TagId) => boolean =
    composeSelector(getStateTags, tags.applyPremiumStyleToFilter);

  // @ts-ignore
  const isSectionPreviewEnabled: (arg0: State, arg1: TagId) => boolean =
    composeSelector(getStateTags, tags.isSectionPreviewEnabled);

  const showAssignmentSelfMarkedQuestions: (
    arg0: State,
    arg1: TagId
  ) => boolean = composeSelector(
    getStateTags,
    tags.showAssignmentSelfMarkedQuestions
  );

  const getParentId: (arg0: State, arg1: TagId) => string = composeSelector(
    getStateTags,
    tags.getParentId
  );

  // @ts-ignore
  const getCategoryTagIds: (arg0: State, arg1: CategoryId) => List<TagId> =
    // @ts-ignore
    composeSelector(getStateCategories, categories.getCategoryTagIds);

  const findTagIdByName: (
    arg0: State,
    arg1: string
  ) => string | null | undefined = composeSelector(
    getStateTags,
    tags.findTagIdByName
  );

  const getCategoryValues = createSelector(
    // @ts-ignore
    getStateCategories,
    categories.getCategoryValues
  );

  const getAllTagIds = createSelector(
    getCategoryValues,
    categories.getAllTagIdsSelector
  );

  const findCategoryIdByName: (arg0: State, arg1: Name) => string =
    // @ts-ignore
    composeSelector(getStateCategories, categories.findCategoryIdByName);

  const showCardAdTagIdsOnYourCoursesPage = createSelector(
    getStateTags,
    tagsState => {
      const tagIds = tagsState.keySeq();
      return tagIds.filter(tagId =>
        tags.showCardAdOnYourCoursesPage(tagsState, tagId)
      );
    }
  );

  const getRootCategoriesDropdownOptions = createSelector(
    getRootCategoryIds,
    getStateCategories,
    getStateTags,
    (categoryIds, categoriesState, tagsState) => {
      return categoryIds.map(categoryId => {
        const tagIds = categories.getCategoryTagIds(
          // @ts-ignore
          categoriesState,
          categoryId
        );
        const dropdownOptions = tagIds.map(tagId => ({
          value: tagId,
          label: tags.getTagName(tagsState, tagId),
          hidden: tags.isTagHidden(tagsState, tagId)
        }));

        return Map({
          id: categoryId,
          name: categories.getCategoryName(categoriesState as any, categoryId),
          hidden: categories.isCategoryHidden(
            categoriesState as any,
            categoryId
          ),
          semanticCategory: categories.getSemanticCategory(
            categoriesState as any,
            categoryId
          ),
          dropdownOptions
        });
      });
    }
  );

  const getRootTagHierarchy = createSelector(
    getRootTag,
    getStateCategories,
    getStateTags,
    (rootTag, categoriesState, tagsState) => {
      return getTagHierarchy(categoriesState, tagsState, rootTag.id!);
    }
  );

  const getTagHierarchy: any = (
    categoriesState: any,
    tagsState: any,
    tagId: string
  ) => {
    const childCategoryIds = tags.getCategoryIds(tagsState, tagId);
    return Map({
      id: tagId,
      type: "tag",
      title: tags.getTagName(tagsState, tagId),
      hidden: tags.isTagHidden(tagsState, tagId),
      children: childCategoryIds.map(categoryId =>
        getCategoryHierarchy(categoriesState, tagsState, categoryId)
      ),
      isQuizOnly: tags.getTagCoursesFilterOnly(tagsState, tagId) === false
    });
  };

  const getCategoryHierarchy = (
    categoriesState: any,
    tagsState: any,
    categoryId: string
  ) => {
    const childTagIds = categories.getCategoryTagIds(
      categoriesState,
      categoryId
    );
    return Map({
      id: categoryId,
      type: "category",
      title: categories.getCategoryName(categoriesState, categoryId),
      hidden: categories.isCategoryHidden(categoriesState, categoryId),
      children: childTagIds.map(tagId =>
        getTagHierarchy(categoriesState, tagsState, tagId)
      )
    });
  };

  const makeGetCategoryTags = (
    categoryId: string
  ): ((arg0: State) => List<Tag>) =>
    // @ts-ignore
    createSelector(
      (state: any) => getCategoryTagIds(state, categoryId),
      getStateTags,
      (tagIds, stateTags) => tagIds.map(tagId => tags.getTag(stateTags, tagId))
    );

  // @ts-ignore
  const getCategorySemanticCategory: (
    arg0: State,
    arg1: CategoryId
  ) => SemanticCategory =
    // @ts-ignore
    composeSelector(getStateCategories, categories.getSemanticCategory);

  const getCategoryIdBySemanticCategory: (
    arg0: State,
    arg1: CategoryId
  ) => SemanticCategory =
    // @ts-ignore
    composeSelector(
      getStateCategories,
      categories.findCategoryIdSemanticMeaning
    );

  const getTagSemanticCategory: (arg0: State, arg1: TagId) => SemanticCategory =
    composeSelector(getStateTags, tags.getSemanticCategory);

  return {
    getCategoriesSelector,
    getTagsSelector,
    getStateTags,
    getStateCategories,
    getAllTags: getStateTags,
    getCategoryIds,
    getRootTag,
    hasRootTag,
    getRootCategoryIds,
    getCategory,
    getCategoryName,
    getTagName,
    getTagDescription,
    getTagSubjectGroup,
    getTagCategoryIds,
    getVisibleTagCategoryIds,
    getVisibleRootCategoryIds,
    getVisibleRootCategoryIdsWithoutPrice,
    getFirstRootCategoryId,
    getTagDetails,
    getCategoryDetails,
    applyPremiumStyleToFilter,
    isSectionPreviewEnabled,
    showAssignmentSelfMarkedQuestions,
    getTagCoursesFilterOnly,
    getParentId,
    getCategoryTagIds,
    findTagIdByName,
    getCategoryValues,
    getAllTagIds,
    findCategoryIdByName,
    showCardAdTagIdsOnYourCoursesPage,
    getRootCategoriesDropdownOptions,
    getRootTagHierarchy,
    getTagHierarchy,
    getCategoryHierarchy,
    makeGetCategoryTags,
    getCategorySemanticCategory,
    getTagSemanticCategory,
    getCategoryIdBySemanticCategory
  };
}

export const {
  getCategoriesSelector,
  getTagsSelector,
  getStateTags,
  getStateCategories,
  getAllTags,
  getCategoryIds,
  getRootTag,
  hasRootTag,
  getRootCategoryIds,
  getCategory,
  getCategoryName,
  getTagName,
  getTagDescription,
  getTagSubjectGroup,
  getTagCategoryIds,
  getVisibleTagCategoryIds,
  getVisibleRootCategoryIds,
  getVisibleRootCategoryIdsWithoutPrice,
  getFirstRootCategoryId,
  getTagDetails,
  getCategoryDetails,
  applyPremiumStyleToFilter,
  isSectionPreviewEnabled,
  showAssignmentSelfMarkedQuestions,
  getTagCoursesFilterOnly,
  getParentId,
  getCategoryTagIds,
  findTagIdByName,
  getCategoryValues,
  getAllTagIds,
  findCategoryIdByName,
  showCardAdTagIdsOnYourCoursesPage,
  getRootCategoriesDropdownOptions,
  getRootTagHierarchy,
  getTagHierarchy,
  getCategoryHierarchy,
  makeGetCategoryTags,
  getCategorySemanticCategory,
  getTagSemanticCategory,
  getCategoryIdBySemanticCategory
} = makeSelectors(getTagsFeatureSlice);
